import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LanguageService } from '../../../core/services/language.service';

interface EditorialBanner {
  enabled: boolean;
  title: string;
  body: string;
  href?: string;
  cta_label?: string;
}

@Component({
  selector: 'app-editorial-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `@if (banner(); as item) {
    @if (!dismissed()) {
      <aside
        class="fixed bottom-4 right-4 z-50 w-[min(28rem,calc(100vw-2rem))] rounded-2xl border border-cyan-400/30 bg-slate-950 p-5 text-white shadow-2xl"
        role="status"
        aria-live="polite"
      >
        <button
          type="button"
          class="absolute right-3 top-2 text-xl text-slate-400 hover:text-white"
          aria-label="Fermer l’annonce"
          (click)="dismissed.set(true)"
        >
          ×
        </button>
        <p class="pr-6 font-semibold">{{ item.title }}</p>
        <p class="mt-1 text-sm text-slate-300">{{ item.body }}</p>
        @if (safeHref(item.href); as href) {
          <a class="mt-3 inline-flex text-sm font-semibold text-cyan-300 hover:text-cyan-200" [href]="href"
            >{{ item.cta_label || 'En savoir plus' }} →</a
          >
        }
      </aside>
    }
  }`,
})
export class EditorialBannerComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly language = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly languageChanges = toObservable(this.language.currentLang);
  readonly banner = signal<EditorialBanner | null>(null);
  readonly dismissed = signal(false);

  ngOnInit(): void {
    if (!this.browser) return;
    this.languageChanges
      .pipe(
        distinctUntilChanged(),
        switchMap((locale) =>
          this.http
            .get<{ payload: unknown }>(`${environment.apiUrl}/content/${locale}/site.banner`)
            .pipe(catchError(() => of({ payload: null })))
        ),
        map((response) => this.validate(response.payload)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((banner) => {
        this.banner.set(banner);
        this.dismissed.set(false);
      });
  }

  safeHref(value?: string): string | null {
    if (!value) return null;
    if (value.startsWith('/') && !value.startsWith('//')) return value;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' &&
        (url.hostname === 'ancelade.cloud' ||
          url.hostname === 'ancelade.com' ||
          url.hostname.endsWith('.ancelade.cloud'))
        ? url.toString()
        : null;
    } catch {
      return null;
    }
  }

  private validate(value: unknown): EditorialBanner | null {
    if (typeof value !== 'object' || value === null) return null;
    const item = value as Record<string, unknown>;
    if (item['enabled'] !== true || typeof item['title'] !== 'string' || typeof item['body'] !== 'string') return null;
    return {
      enabled: true,
      title: item['title'].slice(0, 160),
      body: item['body'].slice(0, 500),
      href: typeof item['href'] === 'string' ? item['href'] : undefined,
      cta_label: typeof item['cta_label'] === 'string' ? item['cta_label'].slice(0, 80) : undefined,
    };
  }
}
